import { useState, useCallback } from 'react'
import { receptionCustomService } from 'services'
import { CheckoutAdditionalField } from '../types'

type UseCreateCheckoutAdditionalFieldsOptions = {
  onSuccess?: () => void
  onError?: (error: Error) => void
}

type UseCreateCheckoutAdditionalFieldsReturn = {
  submit: (fields: CheckoutAdditionalField[]) => Promise<void>
  isSubmitting: boolean
  error: Error | null
}

/**
 * チェックアウト追加フィールドを保存するフック
 */
export const useCreateCheckoutAdditionalFields = (
  tenantId: number | null,
  options?: UseCreateCheckoutAdditionalFieldsOptions
): UseCreateCheckoutAdditionalFieldsReturn => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const submit = useCallback(
    async (fields: CheckoutAdditionalField[]) => {
      if (!tenantId) {
        const err = new Error('テナントIDが指定されていません')
        setError(err)
        options?.onError?.(err)
        return
      }

      setIsSubmitting(true)
      setError(null)

      try {
        const fieldsToSubmit: CheckoutAdditionalField[] = fields.map((field) => ({
          ...(field.id && { id: field.id }),
          inputType: field.inputType,
          requiredFlag: field.requiredFlag,
          displayFlag: field.displayFlag,
          displayName: field.displayName,
          ...(field.options && field.options.length > 0 && {
            options: field.options
              .filter((option) => option.optionName?.trim())
              .map((option) => ({
                optionName: option.optionName,
                displayFlag: option.displayFlag,
              })),
          }),
        }))

        await receptionCustomService.createCheckoutAdditionalFields(tenantId, fieldsToSubmit)

        options?.onSuccess?.()
      } catch (err) {
        let errorMessage = '登録に失敗しました'

        if (err instanceof Error) {
          errorMessage = err.message
        } else if (
          err &&
          typeof err === 'object' &&
          'error' in err &&
          typeof err.error === 'string'
        ) {
          errorMessage = err.error
        } else if (
          err &&
          typeof err === 'object' &&
          'message' in err &&
          typeof err.message === 'string'
        ) {
          errorMessage = err.message
        }

        const error = new Error(errorMessage)
        setError(error)
        options?.onError?.(error)
      } finally {
        setIsSubmitting(false)
      }
    },
    [tenantId, options]
  )

  return {
    submit,
    isSubmitting,
    error,
  }
}
